import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { twMerge } from 'tailwind-merge';

import {
  useCopyToast,
  BaseButton,
  CloseButton,
  formatDateToMonthDayYear,
  getTimeAgo,
  UserBadge,
  useScrollLock,
  useAuth,
} from '@repo/common';
import { getUserProfileById } from '../../api/user/user.api';
import { getOtherAppliesCount } from '../../api/jobboard/apply.api';
import { getOtherBookingCount } from '../../api/booking/booking.api';

import { ReactComponent as HumanIcon } from '@repo/common/assets/icons/48px/icon_48px_human.svg';
import { ReactComponent as CopyIcon } from '@repo/common/assets/icons/16px/icon_16px_copy.svg';
import { ReactComponent as UserProfileModalActive } from '@repo/common/assets/images/userProfileModalActive.svg';
import { ReactComponent as UserProfileModalInactive } from '@repo/common/assets/images/userProfileModalInactive.svg';
import profileRingCreator from '@repo/common/assets/images/profile_ring_creator.svg';
import profileRingPathfinder from '@repo/common/assets/images/profile_ring_pathfinder.svg';

const PROFILE_RINGS = {
  creator: profileRingCreator,
  pathfinder: profileRingPathfinder,
};
const MOBILE_CLOSE_ANIMATION_MS = 300;

const Avatar = ({ profileUrl, alt, className }) => {
  if (
    !profileUrl ||
    profileUrl.includes('null') ||
    profileUrl.includes('undefined')
  ) {
    return (
      <HumanIcon className={twMerge('text-grayscale-gray06', className)} />
    );
  }

  const finalSrc =
    profileUrl.startsWith('data:image/') && profileUrl.includes('http')
      ? profileUrl.substring(profileUrl.indexOf('http'))
      : !profileUrl.startsWith('http') && !profileUrl.startsWith('data:image/')
        ? `data:image/jpeg;base64,${profileUrl}`
        : profileUrl;

  return (
    <img
      src={finalSrc}
      alt={alt || 'profile'}
      className={twMerge('rounded-full object-cover', className)}
      onError={(e) => {
        e.target.style.display = 'none';
      }}
    />
  );
};

const UserProfileModal = ({ data, onClose }) => {
  const profileBg = `${process.env.REACT_APP_ASSETS_URL}/MyPage/img-profile-bg.svg`;
  const [userInfo, setUserInfo] = useState(null);
  const [applicationCount, setApplicationCount] = useState(0);
  const [workshopCount, setWorkshopCount] = useState(0);
  const [flag, setFlag] = useState('');
  const { copy, copyToast } = useCopyToast();
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef(null);

  const { user } = useAuth();
  const isAdmin = user?.role === 'A';
  const lastLoginAt = userInfo?.lastLoginAt;
  const badgeName = String(userInfo?.badgeName ?? '').toLowerCase();
  const profileRing = PROFILE_RINGS[badgeName];
  const stepLabel = String(userInfo?.step ?? 1).padStart(2, '0');

  const isActive = lastLoginAt
    ? (new Date() - new Date(lastLoginAt)) / (1000 * 60 * 60 * 24) <= 90
    : false;

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const userId = data?.id;

        const userInfoData = await getUserProfileById(userId);
        const applicationData = await getOtherAppliesCount(userId);
        const workshopData = await getOtherBookingCount(userId);

        setUserInfo(userInfoData);
        setApplicationCount(applicationData);
        setWorkshopCount(workshopData);
      } catch (error) {
        console.error('Failed to fetch QnA detail', error);
      }
    };

    fetchUserInfo();
  }, [data.id]);

  useEffect(() => {
    if (!userInfo?.nationality) return;
    fetch(`https://restcountries.com/v3.1/name/${userInfo.nationality}`)
      .then((res) => res.json())
      .then((result) => {
        setFlag(result[0]?.flag || '');
      })
      .catch(() => setFlag(''));
  }, [userInfo?.nationality]);

  useScrollLock();

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const handleClose = () => {
    if (isClosing) return;

    const isDesktop = window.innerWidth >= 768;
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    if (isDesktop || prefersReducedMotion) {
      onClose();
      return;
    }

    setIsClosing(true);
    closeTimerRef.current = setTimeout(onClose, MOBILE_CLOSE_ANIMATION_MS);
  };

  const handleCopy = (email) => copy(email);

  return ReactDOM.createPortal(
    <div
      className={`fixed inset-0 z-[10000000] flex flex-col items-center justify-end bg-surface-black40 transition-opacity duration-300 ease-out motion-reduce:transition-none tabletH:justify-center tabletH:bg-surface-black60 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <div className='absolute inset-0' onClick={handleClose} />

      <div
        className={`relative flex min-h-[470px] max-h-[800px] w-full flex-col items-center overflow-hidden rounded-t-[40px] bg-grayscale-gray00 shadow-3 motion-reduce:animate-none tabletH:animate-none tabletH:rounded-[30px] tabletH:bg-grayscale-gray01 tabletH:p-2 tabletH:pb-6 tabletH:shadow-none ${
          isClosing ? 'animate-slideDown' : 'animate-slideUp'
        } ${
          isAdmin
            ? 'tabletH:min-h-[595px] tabletH:max-w-[560px]'
            : 'tabletH:max-w-[480px]'
        }`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className='flex w-full items-center justify-center py-2 tabletH:hidden'>
          <div className='h-1 w-9 rounded-[999px] bg-grayscale-gray03' />
        </div>

        <div className='flex w-full flex-col items-center px-4 pb-10 pt-2 tabletH:p-0'>
          <div className='relative flex w-full items-center justify-between bg-grayscale-gray00 tabletH:bg-grayscale-gray01'>
            <div className='relative flex h-[140px] w-full justify-center items-center rounded-[25px] bg-grayscale-gray00 overflow-hidden shadow-3'>
              <div
                className='absolute inset-0 m-auto'
                style={{
                  width: '286px',
                  height: '186px',
                  aspectRatio: '143 / 93',
                  opacity: 1,
                  mixBlendMode: 'exclusion',
                  backgroundImage: `url(${profileBg})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: '0px -0.186px',
                  backgroundSize: '100% 103.405%',
                }}
              />
            </div>
            <div className='absolute right-6 top-6 hidden tabletH:block'>
              <CloseButton type='default' onClick={handleClose} />
            </div>
          </div>

          <div className='z-[10] -mt-[70px] flex w-full items-start gap-2 px-5 pb-6 pt-0 tabletH:px-6'>
            <div className='relative size-[130px] shrink-0 rounded-full p-[5px] shadow-3'>
              <Avatar
                profileUrl={data.profileUrl}
                className='size-[120px] border-[5px] border-grayscale-gray00 rounded-[500px] shadow-3'
              />
              {profileRing && (
                <img
                  src={profileRing}
                  alt=''
                  aria-hidden='true'
                  className='pointer-events-none absolute inset-0 size-[130px]'
                />
              )}
            </div>

            <div
              className={`min-w-0 flex-1 self-stretch ${
                isAdmin ? 'flex flex-col gap-1.5' : ''
              }`}
            >
              <div className='pt-20 typo-title3 text-text-primary break-words tabletH:typo-title2'>
                {isAdmin && (
                  <BaseButton
                    isStatic
                    size='s'
                    type='text'
                    bgclass='bg-surface-black40/40 py-1 pl-2 pr-1.5 h-6'
                    borderclass='border border-grayscale-gray05'
                    textclass='text-text-inverse typo-b12Strong'
                    gap='gap-0.5'
                    width='w-[77px]'
                    icon={
                      isActive ? (
                        <UserProfileModalActive />
                      ) : (
                        <UserProfileModalInactive />
                      )
                    }
                  >
                    {isActive ? 'ACTIVE' : 'INACTIVE'}
                  </BaseButton>
                )}
                <span>
                  {[data.firstName, data.lastName].filter(Boolean).join(' ')}
                </span>
                <UserBadge
                  badgeName={badgeName}
                  size='s'
                  className='ml-1 align-middle tabletH:ml-[6px]'
                />
              </div>
              {isAdmin && (
                <div
                  className='flex gap-0.5 items-center text-text-secondary typo-b14 cursor-pointer'
                  onClick={() =>
                    handleCopy(userInfo?.email || userInfo?.socialEmail)
                  }
                >
                  {userInfo?.email || userInfo?.socialEmail}
                  <CopyIcon />
                </div>
              )}
            </div>
          </div>

          <div
            className={`flex w-full flex-col items-start gap-6 px-5 tabletH:px-6 ${
              isAdmin && 'pb-6'
            }`}
          >
            <div className='flex w-full flex-wrap items-start gap-1.5 tabletH:gap-2'>
              <BaseButton
                type='text'
                size='s'
                bgclass='h-6 min-w-0 bg-green-light01 px-2 py-1 tabletH:h-7 tabletH:px-3 tabletH:py-1.5'
                textclass='text-green-deep04'
                hideIcon
                isStatic
              >
                <span className='text-green-deep01 mr-1'>Lv</span>step{' '}
                {stepLabel}
              </BaseButton>

              {userInfo?.careerRole &&
                userInfo?.careerRole.trim() !== '' &&
                userInfo?.visibility?.careerRole === 'PUBLIC' && (
                  <BaseButton
                    type='text'
                    size='s'
                    bgclass='h-6 min-w-0 bg-grayscale-gray01 px-2 py-1 tabletH:h-7 tabletH:bg-grayscale-gray02 tabletH:px-3 tabletH:py-1.5'
                    hideIcon
                    width='w-fit'
                    isStatic
                  >
                    {userInfo?.careerRole}
                  </BaseButton>
                )}

              {userInfo?.visaInfo?.type &&
                userInfo?.visaInfo?.type?.trim() !== '' &&
                userInfo?.visibility?.visaInfo === 'PUBLIC' && (
                  <BaseButton
                    type='text'
                    size='s'
                    bgclass='h-6 min-w-0 bg-grayscale-gray01 px-2 py-1 tabletH:h-7 tabletH:bg-grayscale-gray02 tabletH:px-3 tabletH:py-1.5'
                    hideIcon
                    width='w-fit'
                    isStatic
                  >
                    {userInfo?.visaInfo.type}
                  </BaseButton>
                )}

              {userInfo?.nationality &&
                userInfo?.nationality.trim() !== '' &&
                userInfo?.visibility?.nationality === 'PUBLIC' && (
                  <BaseButton
                    type='text'
                    size='s'
                    bgclass='h-6 min-w-0 bg-grayscale-gray01 px-2 py-1 tabletH:h-7 tabletH:bg-grayscale-gray02 tabletH:px-3 tabletH:py-1.5'
                    hideIcon
                    width='w-fit'
                    isStatic
                  >
                    {flag} {userInfo?.nationality}
                  </BaseButton>
                )}
            </div>

            <div className='flex w-full flex-col items-start gap-1 rounded-[15px] bg-grayscale-gray00 pb-6 pt-4 tabletH:px-6'>
              <div className='typo-b12 text-text-secondary'>My Goal is ...</div>
              <div
                className='w-full typo-b16Strong break-words whitespace-pre-wrap text-text-secondary'
                style={{
                  lineHeight: '30px',
                  minHeight: '90px',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='30'%3E%3Cline x1='0' y1='29.5' x2='100%25' y2='29.5' stroke='%234DD5A3' stroke-width='1' stroke-dasharray='4 4'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'repeat-y',
                  backgroundSize: '100% 30px',
                }}
              >
                {userInfo?.goal || ''}
              </div>
            </div>

            {isAdmin && (
              <div className='flex flex-col items-start gap-1 text-text-placeholder typo-b12'>
                Joined: {formatDateToMonthDayYear(userInfo?.createdAt)} / Last
                active: {getTimeAgo(userInfo?.lastLoginAt)}
                <div>
                  Applications : {applicationCount} / Workshops: {workshopCount}
                </div>
              </div>
            )}
          </div>

          {isAdmin && (
            <div className='flex px-6 items-start gap-2 w-full'>
              <BaseButton
                type='text_basic'
                size='l'
                hideIcon
                width='160px'
                onClick={() => {
                  const email = userInfo?.email || userInfo?.socialEmail;
                  if (email) window.location.href = `mailto:${email}`;
                }}
              >
                Contact
              </BaseButton>
              <BaseButton
                type='text_basic'
                size='l'
                hideIcon
                width='160px'
                isDisabled
              >
                Suspend account
              </BaseButton>
              <BaseButton
                type='text_basic'
                size='l'
                hideIcon
                width='160px'
                isDisabled
              >
                View full page
              </BaseButton>
            </div>
          )}
        </div>
      </div>

      {copyToast}
    </div>,
    document.body,
  );
};

export default UserProfileModal;
